import { Controller, FieldErrors, UseControllerProps } from '@graphcommerce/ecommerce-ui'
import { ProductInterface } from '@graphcommerce/graphql-mesh'
import {
  Selected,
  useConfigurableContext,
} from '@graphcommerce/magento-product-configurable/ConfigurableContext/ConfigurableContext'
import { SwatchSize } from '@graphcommerce/magento-product-configurable/Swatches/types'

import { BaseTextFieldProps, FormHelperText, MenuItem, SxProps, Tooltip } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { StandardDropdown } from '../../Dropdown/StandardDropdown'

export type ConfigurableOptionsInputProps = {
  sku: string
  product: Pick<ProductInterface, 'name'>
  errors?: FieldErrors
  size?: SwatchSize
  sx?: SxProps
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & UseControllerProps<any> &
  Pick<BaseTextFieldProps, 'FormHelperTextProps' | 'helperText'> & {
    optionEndLabels?: Record<string, React.ReactNode>
  }

export function ConfigurableOptionsInput(props: ConfigurableOptionsInputProps) {
  const { sku, FormHelperTextProps, errors, name, ...controlProps } = props

  const { options, selection, select, getVariants } = useConfigurableContext(sku)
  const [initialSelectionsInit, setInitialSelectionsInit] = useState(false)

  // This may not always work, coz some combinations may not exist, but in our case we don't have inventory
  useEffect(() => {
    const initialSelection = {}
    options?.forEach((option) => {
      if (option?.attribute_code) {
        initialSelection[option.attribute_code] = option.values?.at(0)?.uid || null
      }
    })
    select((prev) => ({ ...prev, ...initialSelection } as Selected))
    setInitialSelectionsInit(true)
  }, [select, options])

  const optionLabelRenderer = useCallback((option, label, index) => {
    const swatchData = option.additionalData?.swatch_data

    if (index === 0 && swatchData) {
      if (swatchData.__typename === 'ColorSwatchData') {
        return (
          <div className='grid grid-cols-[30fr_70fr] items-center justify-items-start gap-[10px]'>
            <div>{option.label || ''}</div>
            <div
              className='inline-block h-[22px] w-full min-w-[50px] border-[0.5px] border-20-grey'
              style={{ backgroundColor: swatchData.value }}
            />
          </div>
        )
      }
      if (swatchData.__typename === 'TextSwatchData') {
        return swatchData.value
      }
    }
    return <>{label}</>
  }, [])

  const optionsSorted = Array.isArray(options)
    ? [...options]?.sort((o1, o2) => ((o1?.position || 0) > (o2?.position || 0) ? 1 : -1))
    : []
  if (!initialSelectionsInit) return null

  return (
    <div className='flex flex-col gap-[15px]'>
      {optionsSorted?.map((option) => {
        if (!option?.uid || !option.attribute_code) return null

        const { attribute_code } = option
        const error = errors?.[attribute_code]
        // Here we will get the product setup cost from product object
        const breakpointOptions =
          option.values
            // For 4over products, we sort attribute values in alphabetical order
            ?.sort((a, b) =>
              sku?.includes('4over')
                ? (a?.store_label || '').localeCompare(b?.store_label || '')
                : 0,
            )
            .map((o) => ({
              label: o?.store_label || '',
              value: o?.uid || '',
              additionalData: { swatch_data: o?.swatch_data || {} },
              hasVariants:
                option.attribute_code &&
                getVariants({
                  ...selection,
                  [option?.attribute_code]: o?.uid?.toString() as unknown as number,
                }).length > 0,
            })) || []

        const breakpointMenuItems = breakpointOptions.map((bo) => (
          <MenuItem
            value={bo.value}
            className='flex justify-start pr-5'
            sx={{
              justifyContent: 'flex-start',
            }}
            disabled={!bo.hasVariants}
          >
            {bo.label?.length > 50 ? (
              <Tooltip title={bo.label}>
                <span className='max-w-[350px] truncate'>{bo.label}</span>
              </Tooltip>
            ) : (
              <span className='max-w-[350px] truncate'>{bo.label}</span>
            )}

            {/* <span>{bo.additionalData?.swatch_data?.toString()}</span> */}
          </MenuItem>
        ))
        return (
          <Controller
            key={option.uid}
            defaultValue={selection[attribute_code] ?? ''}
            name={`${name}[${attribute_code}]`}
            {...controlProps}
            render={({
              field: { onChange, value, name: inputName, ref, onBlur },
              fieldState: { error: errorHelperText },
            }) => (
              <>
                {/* <Dropdown
                  key={option?.uid}
                  options={
                    option.values?.map((o) => ({
                      label: o?.store_label || '',
                      value: o?.uid || '',
                      additionalData: { swatch_data: o?.swatch_data || {} },
                    })) || []
                  }
                  className='w-full'
                  value={value}
                  label={option.label || ''}
                  onChange={(selectedOption) => {
                    onChange(selectedOption.value)
                    select(
                      (prev) => ({ ...prev, [attribute_code]: selectedOption.value } as Selected),
                    )
                  }}
                  placeholder={i18n._('Select {label}', { label: option.label })}
                  optionLabelRenderer={optionLabelRenderer}
                /> */}
                <StandardDropdown
                  value={value}
                  label={option.label || ''}
                  onChange={(selectedOption) => {
                    onChange(selectedOption?.target?.value)
                    select(
                      (prev) =>
                        ({ ...prev, [attribute_code]: selectedOption?.target?.value } as Selected),
                    )
                  }}
                  options={breakpointMenuItems}
                  twoColumns
                  fullWidth
                />
                {error && (
                  <FormHelperText
                    error
                    {...FormHelperTextProps}
                    sx={{
                      position: 'absolute',
                    }}
                  >
                    {`${option.label} is ${error?.type}`}
                  </FormHelperText>
                )}
              </>
            )}
          />
        )
      })}
    </div>
  )
}
